<script setup lang="ts">
const {
  isLoading = false,
  roundCorners = false,
  borderless = false,
  borderlessHeader = false,
  centeredBody = false,
  noPaddingBody = false,
  disableButtons = false,
  spaceBetweenButtons = false,
  fullwidthButtonsMobile = true,
  reverseButtonsMobile = false,
  greyBackground = false,
} = defineProps<{
  isLoading?: boolean;
  roundCorners?: boolean;
  borderless?: boolean;
  borderlessHeader?: boolean;
  centeredBody?: boolean;
  noPaddingBody?: boolean;
  disableButtons?: boolean;
  spaceBetweenButtons?: boolean;
  fullwidthButtonsMobile?: boolean;
  reverseButtonsMobile?: boolean;
  greyBackground?: boolean;
}>();

const slots = useSlots();

const hasHeaderSlot = computed(() => !!slots.header);
const hasBodySlot = computed(() => !!slots.body);
const hasButtonsSlot = computed(() => {
  return disableButtons !== true && !!slots.buttons;
});
const hasActionsSlot = computed(() => !!slots.actions);
const cardClass = computed(() => ({
  "card-loading": isLoading === true,
  rounded: roundCorners,
  borderless: borderless,
  "borderless-header": borderlessHeader,
  "centered-body": centeredBody,
  "nopadding-body": noPaddingBody,
  "space-between-buttons": spaceBetweenButtons,
  "fw-buttons-mobile": fullwidthButtonsMobile,
  "reverse-buttons-mobile": reverseButtonsMobile,
  "grey-background": greyBackground,
  "has-actions": !!slots.actions,
}));
</script>

<template>
  <div class="card" :class="cardClass">
    <div class="card-header" v-if="hasHeaderSlot">
      <slot name="header"></slot>
    </div>
    <div class="card-body" v-if="hasBodySlot">
      <slot name="body"></slot>
    </div>
    <div class="card-buttons" v-if="hasButtonsSlot">
      <slot name="buttons"></slot>
    </div>
    <div class="card-actions" v-if="hasActionsSlot">
      <slot name="actions"></slot>
    </div>
    <LoadingSpinner v-if="isLoading" />
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */
/* stylelint-disable no-descending-specificity */

/* TODO: fix all the specificity issues */
.card {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--color-border);

  &.rounded {
    border-radius: var(--border-radius);

    .card-body {
      border-radius: var(--border-radius);
    }
  }

  &.borderless {
    border: none;

    .card-header {
      border-bottom: none;
    }
  }

  &.borderless-header {
    .card-header {
      border-bottom: none;
    }
  }

  &.centered-body {
    .card-body {
      align-items: center;
      justify-content: center;
    }
  }

  &.has-actions {
    position: relative;
    margin-bottom: var(--space-40) !important;
  }

  &.space-between-buttons {
    .card-buttons {
      justify-content: space-between;
    }
  }

  &.card-loading {
    overflow: hidden;
    pointer-events: none;

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 9;
      display: block;
      width: 100%;
      height: 100%;
      background-color: rgb(255 255 255 / 60%);
      content: "";
    }

    .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 10;
      margin: 0;
      transform: translate(-50%, -50%);

      :deep(svg) {
        width: 36px;
        height: 36px;
      }
    }
  }

  &.fw-buttons-mobile {
    @include mdx {
      .card-buttons {
        flex-direction: column;

        .button {
          width: 100%;

          + .button {
            margin-top: var(--space-16);
            margin-left: 0;
          }
        }
      }
    }
  }

  &.reverse-buttons-mobile {
    @include mdx {
      .card-buttons {
        flex-direction: column-reverse;

        .button {
          width: 100%;
          margin-top: var(--space-16);

          + .button {
            margin-top: 0;
            margin-left: 0;
          }
        }
      }
    }
  }

  &.grey-background {
    .card-header,
    .card-body,
    .card-buttons {
      background-color: var(--color-grey-surface);
    }
  }

  + .card {
    @include mdx {
      margin-top: var(--space-40);
    }
  }
}

.card-header {
  padding: var(--space-16) var(--space-24);
  border-bottom: 1px solid var(--color-border);

  // background: var(--color-grey-surface);
}

.card-body {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--space-16);
  background: var(--primary-white);

  .form-row:last-child {
    margin-bottom: 0 !important;
  }

  @include md {
    flex-grow: 1;
    padding: var(--space-24);
  }
}

.nopadding-body {
  .card-body {
    padding: 0;
  }
}

.card-buttons {
  display: flex;
  gap: var(--cm-spacing-4, 1rem);
  justify-content: flex-end;
  width: 100%;
  padding: 0 var(--space-16) var(--space-16);
  background: var(--primary-white);

  .button {
    + .button {
      margin-left: var(--space-16);
    }
  }

  @include md {
    padding: 0 var(--space-24) var(--space-24);
  }
}

.card-actions {
  position: absolute;
  top: 100%;
  left: 0;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: var(--space-16) 0 0;

  .button {
    + .button {
      margin-left: var(--space-16);
    }
  }
}
</style>
